import { inject, Injectable } from '@angular/core';
import {
  CheckoutTab,
  CheckoutTabsDisabled,
  CheckoutTotals,
  FormInputAnswers,
  ProcessContractDto,
  ProcessContractResponse,
  SaveAddressDto,
  UserAddress
} from '../models';
import { NgxAimService } from '../ngx-aim.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AddressType, CheckoutTabNames, CheckoutType, GAEventAction } from '../enums';
import { TaxAmountDto, TotalBeforeTaxDto } from '../models/dto/tax.dto';
import { HttpErrorService } from './http-error.service';
import { ContractsService } from './contracts.service';
import { GenericCheckoutService } from './generic-checkout.service';
import { PaymentService } from './payment.service';
import { GoogleAnalyticsService } from './google-analytics.service';

@Injectable({
  providedIn: 'root'
})
export class ContractsCheckoutService implements GenericCheckoutService {
  ngxAimService = inject(NgxAimService);
  http = inject(HttpClient);
  contractsService = inject(ContractsService);
  httpErrorService = inject(HttpErrorService);
  paymentService = inject(PaymentService);
  gaService = inject(GoogleAnalyticsService);

  private baseUrl = this.ngxAimService.getApiUrl();

  public tabs: CheckoutTab[] = [];

  private tabIndexSubject = new BehaviorSubject<number>(0);
  public tabIndex$ = this.tabIndexSubject.asObservable();
  public setTabIndex(index: number) {
    this.tabIndexSubject.next(index);
  }
  public setTabIndexByHeader(header: CheckoutTabNames) {
    const tab = this.tabs.find((tab) => tab.header === header);
    if (tab) {
      this.tabIndexSubject.next(tab.index);
    } else {
      console.error(`unable to find tab header: ${header}`);
    }
  }

  private tabsDisabledSubject = new BehaviorSubject<CheckoutTabsDisabled>({
    [CheckoutTabNames.BILLING_ADDRESS]: false,
    [CheckoutTabNames.PAYMENT]: true,
    [CheckoutTabNames.OVERVIEW]: true,
  });
  public tabsDisabed$ = this.tabsDisabledSubject.asObservable();
  public setTabIsDisabled(tabIndex: CheckoutTabNames, isDisabled: boolean) {
    const tabs = this.tabsDisabledSubject.value;
    tabs[tabIndex] = isDisabled;
    this.tabsDisabledSubject.next(tabs);
  }

  private isLoadingTotalsSubject = new BehaviorSubject<boolean>(false);
  public isLoadingTotals$ = this.isLoadingTotalsSubject.asObservable();
  private totalsSubject = new BehaviorSubject<CheckoutTotals>({
    items: 0,
    shipping: 0,
    tax: 0,
    total: 0,
  });
  public totals$ = this.totalsSubject.asObservable();

  public updateTotals() {
    this.calculateTotals();
  }

  async calculateTotals() {
    this.isLoadingTotalsSubject.next(true);
    const shipping = this.contractsService.selectedDelivery?.Price || 0;
    const insturmentTotal = this.getInstrumentTotal();
    const tax = await this.getTaxAmount({ goodsAndServicesAmount: insturmentTotal, shippingAmount: shipping });
    this.totalsSubject.next({
      items: this.getInstrumentTotal(),
      shipping: shipping,
      tax: tax,
      total: insturmentTotal + shipping + tax,
    });
    this.isLoadingTotalsSubject.next(false);
  }

  getAccessoryTotal(): number {
    return this.contractsService.selectedAccessories?.reduce((acc, accessory) => acc + accessory.Price, 0) || 0;
  }

  getInstrumentTotal(): number {
    return this.getAccessoryTotal() +
      (this.contractsService.selectedGrade?.DownPay || 0) +
      (this.contractsService.selectedGrade?.DownMaint || 0);
  }

  saveAddress(address: SaveAddressDto): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/users/save-address`, address);
  }

  getAddress(type: AddressType): Observable<UserAddress> {
    return this.http.get<UserAddress>(`${this.baseUrl}/users/get-address`, {
      params: new HttpParams()
        .set('type', type)
    });
  }

  getTaxAmount(totalBeforeTax: TotalBeforeTaxDto): Promise<number> {
    return new Promise<number>((resolve) => {
      this.http.post<TaxAmountDto>(`${this.baseUrl}/payment/tax`, totalBeforeTax).subscribe({
        next: (res) => resolve(res.amount),
        error: (err) => {
          this.httpErrorService.onHttpError(err, 'Could not calculate sales tax');
          resolve(0);
        }
      });
    });
  }

  checkout(formInputAnswers: FormInputAnswers[]): Observable<ProcessContractResponse> {
    const accessoryIds: number[] = [];
    this.contractsService.selectedAccessories?.forEach(acc => {
      accessoryIds.push(acc.Id);
    });
    this.contractsService.selectedMaintenance?.forEach(maint => {
      accessoryIds.push(maint.Id);
    });
    if (this.contractsService.selectedDelivery) {
      accessoryIds.push(this.contractsService.selectedDelivery.Id);
    }

    const dto: ProcessContractDto = {
      cardData: this.paymentService.cardData,
      TeacherId: this.contractsService.selectedTeacher?.Id,
      SchoolName: this.contractsService.selectedSchool?.SchoolName,
      AIMSchoolId: this.contractsService.selectedSchool?.AIMSchoolId,
      InstrumentId: this.contractsService.selectedInstrument?.InstrumentId,
      RateId: this.contractsService.selectedGrade?.Id,
      AccessoryIdList: accessoryIds,
      accessoryIds: this.contractsService.selectedAccessories?.map(acc => acc.Id) || [],
      maintenanceIds: this.contractsService.selectedMaintenance?.map(maint => maint.Id) || [],
      deliveryId: this.contractsService.selectedDelivery?.Id,
      formInputAnswers: formInputAnswers,
    }
    return this.http.post<ProcessContractResponse>(`${this.baseUrl}/contracts/checkout`, dto);
  }

  public emitGaAddPaymentInfo(): void {
    this.gaService.emitEvent(GAEventAction.ADD_PAYMENT_INFO, {
      currency: 'USD',
      value: this.totalsSubject.value.items,
      payment_type: 'Credit Card',
      checkout_type: CheckoutType.CONTRACT,
      items: [{
        item_id: this.contractsService.selectedInstrument?.InstrumentId || '',
        item_name: this.contractsService.selectedInstrument?.InstrumentName || '',
        price: this.totalsSubject.value.items,
        quantity: 1,
      }],
    });
  }

  emitGaAddShippingInfo(): void {
    this.gaService.emitEvent(GAEventAction.ADD_SHIPPING_INFO, {
      currency: 'USD',
      value: this.totalsSubject.value.items,
      payment_type: 'Credit Card',
      shipping_tier: this.contractsService.selectedDelivery?.AccessoryDesc || '',
      checkout_type: CheckoutType.CONTRACT,
      items: [{
        item_id: this.contractsService.selectedInstrument?.InstrumentId || '',
        item_name: this.contractsService.selectedInstrument?.InstrumentName || '',
        price: this.totalsSubject.value.items,
        quantity: 1,
      }],
    });
  }

  public reset() {
    this.paymentService.reset();
    this.tabIndexSubject.next(0);
    this.tabsDisabledSubject.next({
      [CheckoutTabNames.BILLING_ADDRESS]: false,
      [CheckoutTabNames.PAYMENT]: true,
      [CheckoutTabNames.OVERVIEW]: true,
    });

    this.totalsSubject.next({
      items: 0,
      shipping: 0,
      tax: 0,
      total: 0,
    });
  }
}
